import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TICKER_DESC from '../../data/tickerDescriptions';
import styles from './CommandPalette.module.css';

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  icon: string;
  group: 'page' | 'symbol' | 'action';
  onSelect: () => void;
}

const PAGE_ITEMS: { path: string; label: string; icon: string }[] = [
  { path: '/home', label: 'Dashboard', icon: '🏠' },
  { path: '/positions', label: 'Positions', icon: '📡' },
  { path: '/pnl-monitor', label: 'P&L Monitor', icon: '📊' },
  { path: '/quick-trade', label: 'Quick Trade', icon: '⚡' },
  { path: '/strategist', label: 'AI Strategist', icon: '🧠' },
  { path: '/scalper', label: 'AI Scalper', icon: '🎯' },
  { path: '/trade-calendar', label: 'Trade Calendar', icon: '📅' },
  { path: '/scanner', label: 'Market Scanner', icon: '🔍' },
  { path: '/market', label: 'Market Charts', icon: '📈' },
  { path: '/predictions', label: 'Predictions', icon: '🔮' },
  { path: '/trades', label: 'Trades', icon: '📋' },
  { path: '/reports', label: 'Reports', icon: '📄' },
  { path: '/leaderboard', label: 'Leaderboard', icon: '🏆' },
  { path: '/earnings', label: 'Earnings', icon: '💰' },
  { path: '/errors', label: 'Error Log', icon: '🚨' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
  { path: '/watchlist', label: 'Watchlist', icon: '📋' },
  { path: '/glossary', label: 'Glossary', icon: '📚' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Build symbol items (memoized — ~150 tickers)
  const symbolItems = useMemo<PaletteItem[]>(
    () =>
      Object.entries(TICKER_DESC).map(([symbol, desc]) => ({
        id: `sym-${symbol}`,
        label: symbol,
        hint: desc,
        icon: '📊',
        group: 'symbol' as const,
        onSelect: () => {}, // will be overridden with navigate
      })),
    [],
  );

  // Build all items
  const allItems = useMemo<PaletteItem[]>(() => {
    const pages: PaletteItem[] = PAGE_ITEMS.map((p) => ({
      id: `page-${p.path}`,
      label: p.label,
      hint: p.path,
      icon: p.icon,
      group: 'page',
      onSelect: () => {
        navigate(p.path);
        onClose();
      },
    }));

    const actions: PaletteItem[] = [
      {
        id: 'action-theme',
        label: 'Toggle Dark Mode',
        icon: '🌙',
        group: 'action',
        onSelect: () => {
          document.documentElement.setAttribute(
            'data-theme',
            document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark',
          );
          localStorage.setItem('theme', document.documentElement.getAttribute('data-theme') || 'light');
          onClose();
        },
      },
    ];

    const syms: PaletteItem[] = symbolItems.map((s) => ({
      ...s,
      onSelect: () => {
        navigate(`/quick-trade?symbol=${s.label}`);
        onClose();
      },
    }));

    return [...pages, ...actions, ...syms];
  }, [navigate, onClose, symbolItems]);

  // Filter results
  const filtered = useMemo(() => {
    if (!query.trim()) {
      // Show pages + actions only when empty
      return allItems.filter((i) => i.group !== 'symbol');
    }
    const q = query.toLowerCase();
    return allItems
      .filter((i) => i.label.toLowerCase().includes(q) || (i.hint && i.hint.toLowerCase().includes(q)))
      .slice(0, 20);
  }, [query, allItems]);

  // Group results
  const grouped = useMemo(() => {
    const groups: Record<string, PaletteItem[]> = {};
    for (const item of filtered) {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    }
    return groups;
  }, [filtered]);

  const groupLabels: Record<string, string> = { page: 'Pages', symbol: 'Symbols', action: 'Actions' };
  const groupOrder = ['action', 'page', 'symbol'];

  // Reset state when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Reset active index on filter change
  useEffect(() => {
    setActiveIndex(0);
  }, [filtered]);

  // Scroll active item into view
  useEffect(() => {
    if (!resultsRef.current) return;
    const active = resultsRef.current.querySelector(`.${styles.itemActive}`);
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = useCallback((item: PaletteItem) => {
    item.onSelect();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && filtered[activeIndex]) {
        e.preventDefault();
        handleSelect(filtered[activeIndex]);
      }
    },
    [filtered, activeIndex, onClose, handleSelect],
  );

  if (!open) return null;

  let itemIndex = -1;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.palette} onClick={(e) => e.stopPropagation()}>
        <div className={styles.inputWrap}>
          <span className={styles.searchIcon}>&#128269;</span>
          <input
            ref={inputRef}
            className={styles.input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, symbols, actions..."
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className={styles.kbd}>ESC</kbd>
        </div>

        <div className={styles.results} ref={resultsRef}>
          {filtered.length === 0 && <div className={styles.empty}>No results for "{query}"</div>}
          {groupOrder.map((group) => {
            const items = grouped[group];
            if (!items || items.length === 0) return null;
            return (
              <div key={group} className={styles.group}>
                <div className={styles.groupLabel}>{groupLabels[group]}</div>
                {items.map((item) => {
                  itemIndex++;
                  const idx = itemIndex;
                  return (
                    <button
                      key={item.id}
                      className={`${styles.item} ${idx === activeIndex ? styles.itemActive : ''}`}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <span className={styles.itemIcon}>{item.icon}</span>
                      <span className={styles.itemLabel}>{item.label}</span>
                      {item.hint && <span className={styles.itemHint}>{item.hint}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
